import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  Grid,
  Alert,
  Collapse,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Edit,
  ExpandMore,
  ExpandLess,
  Info,
  Warning,
  TrendingUp
} from '@mui/icons-material';

interface PlannedExpenseMatch {
  id: number;
  title: string;
  category_name: string;
  expense_type: string;
  monthly_amount: number;
  next_due_date: string;
  frequency: string;
  priority: 'high' | 'medium' | 'low' | 'manual_review';
  match_score: number;
}

interface Transaction {
  id: number;
  amount: number;
  description: string;
  date: string;
  category_name: string;
  bank_name: string;
}

interface MatchItem {
  transaction: Transaction;
  plannedExpense: PlannedExpenseMatch;
  autoMatched: boolean;
  status: 'pending' | 'confirmed' | 'rejected' | 'modified';
}

interface PlannedExpenseMatchesPanelProps {
  matches: MatchItem[];
  onConfirmMatch: (matchId: number) => void;
  onRejectMatch: (matchId: number) => void;
  onModifyMatch: (matchId: number, plannedExpenseId: number) => void;
  onRefresh: () => void;
}

const PlannedExpenseMatchesPanel: React.FC<PlannedExpenseMatchesPanelProps> = ({
  matches,
  onConfirmMatch,
  onRejectMatch,
  onModifyMatch,
  onRefresh
}) => {
  const [expanded, setExpanded] = useState(true);
  const [modifyDialogOpen, setModifyDialogOpen] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState<MatchItem | null>(null);
  const [selectedPlannedExpenseId, setSelectedPlannedExpenseId] = useState<number | ''>('');

  const handleModify = (match: MatchItem) => {
    setSelectedMatch(match);
    setModifyDialogOpen(true);
  };

  const handleModifyConfirm = () => {
    if (selectedMatch && selectedPlannedExpenseId) {
      onModifyMatch(selectedMatch.transaction.id, selectedPlannedExpenseId as number);
      setModifyDialogOpen(false);
      setSelectedMatch(null);
      setSelectedPlannedExpenseId('');
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'success';
      case 'medium': return 'warning';
      case 'low': return 'info';
      case 'manual_review': return 'error';
      default: return 'default';
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'high': return <CheckCircle />;
      case 'medium': return <TrendingUp />;
      case 'low': return <Info />;
      case 'manual_review': return <Warning />;
      default: return <Info />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return 'success';
      case 'rejected': return 'error';
      case 'modified': return 'warning';
      case 'pending': return 'info';
      default: return 'default';
    }
  };

  if (matches.length === 0) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Planned Expense Matches
            </Typography>
            <IconButton
              onClick={() => setExpanded(!expanded)}
              size="small"
            >
              {expanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
          <Collapse in={expanded}>
            <Alert severity="info">
              No pending matches found. All transactions have been automatically matched or processed.
            </Alert>
          </Collapse>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Planned Expense Matches ({matches.length})
            </Typography>
            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                size="small"
                onClick={onRefresh}
                startIcon={<TrendingUp />}
              >
                Refresh
              </Button>
              <IconButton
                onClick={() => setExpanded(!expanded)}
                size="small"
              >
                {expanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>
          </Box>

          <Collapse in={expanded}>
            <Grid container spacing={2}>
              {matches.map((match) => (
                <Grid item xs={12} key={match.transaction.id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Box flex={1}>
                          {/* Transaction Info */}
                          <Box mb={2}>
                            <Typography variant="subtitle1" fontWeight="bold">
                              Transaction: {match.transaction.description}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              {match.transaction.category_name} • {match.transaction.bank_name} • 
                              ₹{match.transaction.amount.toFixed(2)} • {new Date(match.transaction.date).toLocaleDateString()}
                            </Typography>
                          </Box>

                          {/* Planned Expense Info */}
                          <Box mb={2}>
                            <Typography variant="subtitle2" color="primary">
                              Matched to: {match.plannedExpense.title}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              {match.plannedExpense.category_name} • {match.plannedExpense.expense_type.replace('_', ' ')} • 
                              ₹{match.plannedExpense.monthly_amount.toFixed(2)} • {match.plannedExpense.frequency}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              Next due: {new Date(match.plannedExpense.next_due_date).toLocaleDateString()}
                            </Typography>
                          </Box>

                          {/* Match Quality */}
                          <Box display="flex" gap={1} alignItems="center" mb={2}>
                            <Chip
                              icon={getPriorityIcon(match.plannedExpense.priority)}
                              label={`${match.plannedExpense.priority.replace('_', ' ').toUpperCase()}`}
                              color={getPriorityColor(match.plannedExpense.priority) as any}
                              size="small"
                            />
                            <Chip
                              label={`Score: ${match.plannedExpense.match_score}/100`}
                              size="small"
                              variant="outlined"
                            />
                            {match.autoMatched && (
                              <Chip
                                label="Auto-Matched"
                                color="success"
                                size="small"
                                variant="outlined"
                              />
                            )}
                            <Chip
                              label={match.status.toUpperCase()}
                              color={getStatusColor(match.status) as any}
                              size="small"
                            />
                          </Box>
                        </Box>

                        {/* Action Buttons */}
                        <Box display="flex" flexDirection="column" gap={1}>
                          {match.status === 'pending' && (
                            <>
                              <Button
                                variant="contained"
                                color="success"
                                size="small"
                                startIcon={<CheckCircle />}
                                onClick={() => onConfirmMatch(match.transaction.id)}
                              >
                                Confirm
                              </Button>
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                startIcon={<Cancel />}
                                onClick={() => onRejectMatch(match.transaction.id)}
                              >
                                Reject
                              </Button>
                              <Button
                                variant="outlined"
                                color="warning"
                                size="small"
                                startIcon={<Edit />}
                                onClick={() => handleModify(match)}
                              >
                                Modify
                              </Button>
                            </>
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Collapse>
        </CardContent>
      </Card>

      {/* Modify Match Dialog */}
      <Dialog open={modifyDialogOpen} onClose={() => setModifyDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Modify Match</DialogTitle>
        <DialogContent>
          <Box mt={2}>
            <Typography variant="body2" color="textSecondary" mb={2}>
              Select a different planned expense to match with this transaction:
            </Typography>
            
            <FormControl fullWidth>
              <InputLabel>Planned Expense</InputLabel>
              <Select
                value={selectedPlannedExpenseId}
                onChange={(e) => setSelectedPlannedExpenseId(e.target.value as number | '')}
                label="Planned Expense"
              >
                {/* This would be populated with available planned expenses */}
                <MenuItem value="">Select a planned expense</MenuItem>
                {/* Add more menu items based on available planned expenses */}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModifyDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleModifyConfirm} 
            variant="contained" 
            disabled={!selectedPlannedExpenseId}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PlannedExpenseMatchesPanel;
